/**
 * AITitleGenerator — edits text lines + per-line colors + style preset and
 * POSTs to /api/homepage-config/title-image/generate. The backend uses Nano
 * Banana to render the painted hero title PNG, which is then persisted to
 * hero.title_image_url_{lang}. Each of the 3 supported languages (PT/EN/ES)
 * gets its own PNG so the hero title matches the active language.
 * Includes an inline HeroImageControls to position the generated PNG on
 * the hero.
 */
import React, { useEffect, useState } from "react";
import { Sparkles, X, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { setPath } from "./setPath.js";
import { ColorField } from "./fields.jsx";
import { HeroImageControls } from "./HeroImagePanel.jsx";

const LANGS = [
  { code: "pt", label: "Português",  flag: "🇵🇹" },
  { code: "en", label: "English",    flag: "🇬🇧" },
  { code: "es", label: "Español",    flag: "🇪🇸" },
];

const DEFAULT_LINES = {
  pt: "APRENDE.\nJOGA.\nVENCE!",
  en: "LEARN.\nPLAY.\nWIN!",
  es: "APRENDE.\nJUEGA.\n¡GANA!",
};

export function AITitleGenerator({ cfg, setCfg }) {
  const [activeLang, setActiveLang] = useState("pt");
  // The source of truth for lines is `hero.title_{lang}` (fallback to defaults).
  const initialLines = ((cfg.hero?.[`title_${activeLang}`]) || DEFAULT_LINES[activeLang]).split("\n");
  const initialColors = cfg.hero?.title_colors || ["#FFFFFF", "#3DA5FF", "#E5FF00"];
  const [lines, setLines] = useState(initialLines);
  const [colors, setColors] = useState(initialColors);
  const [style, setStyle] = useState("painted");
  const [busy, setBusy] = useState(false);

  // Re-hydrate lines when the active language changes or when cfg is refreshed.
  useEffect(() => {
    setLines(((cfg.hero?.[`title_${activeLang}`]) || DEFAULT_LINES[activeLang]).split("\n"));
    setColors(cfg.hero?.title_colors || ["#FFFFFF", "#3DA5FF", "#E5FF00"]);
  }, [activeLang, cfg.hero?.title_pt, cfg.hero?.title_en, cfg.hero?.title_es, cfg.hero?.title_colors]);

  const updateLine  = (i, v) => setLines((arr) => arr.map((x, j) => (j === i ? v : x)));
  const updateColor = (i, v) => setColors((arr) => arr.map((x, j) => (j === i ? v : x)));
  const addLine     = () => setLines((arr) => [...arr, ""]);
  const removeLine  = (i) => { setLines((arr) => arr.filter((_, j) => j !== i)); setColors((arr) => arr.filter((_, j) => j !== i)); };

  const handleGenerate = async () => {
    const cleaned = lines.map((l) => l.trim()).filter(Boolean);
    if (!cleaned.length) { toast.error("Adiciona pelo menos uma linha."); return; }
    setBusy(true);
    try {
      // Update the per-language title text in cfg (so if user reverts to CSS
      // title mode, the text matches). Colors are shared across languages.
      setCfg(setPath(setPath(cfg, ["hero", `title_${activeLang}`], cleaned.join("\n")), ["hero", "title_colors"], colors.slice(0, cleaned.length)));
      const res = await api.post("/homepage-config/title-image/generate", {
        lines: cleaned,
        colors: colors.slice(0, cleaned.length),
        style,
        lang: activeLang,
      });
      const url = (res.data?.title_image_url || "") + `?v=${Date.now()}`;
      // Store BOTH the per-language variant AND the shared field (last-generated
      // becomes the default fallback for languages without their own PNG).
      setCfg((prev) => setPath(setPath(prev, ["hero", `title_image_url_${activeLang}`], url), ["hero", "title_image_url"], url));
      toast.success(`Título ${activeLang.toUpperCase()} gerado (${(res.data?.bytes / 1024).toFixed(0)} KB).`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Geração falhou.");
    } finally { setBusy(false); }
  };

  const handleClear = () => {
    // Clear only the current language variant. If cleared and no fallback,
    // hero renders the CSS title.
    setCfg(setPath(cfg, ["hero", `title_image_url_${activeLang}`], ""));
    toast.info(`Título de imagem ${activeLang.toUpperCase()} removido.`);
  };

  const currentImg = cfg.hero?.[`title_image_url_${activeLang}`] || cfg.hero?.title_image_url || "";
  const fullImgUrl = currentImg.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${currentImg}` : currentImg;
  const isLangSpecific = !!cfg.hero?.[`title_image_url_${activeLang}`];

  return (
    <div className="mt-5 border-t border-slate-100 pt-4" data-testid="ai-title-generator">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-fuchsia-500" /> Título via IA (painted, neon, …) — por idioma
        </Label>
        {currentImg && isLangSpecific && (
          <Button variant="ghost" size="sm" onClick={handleClear} className="text-xs text-slate-500" data-testid="ai-title-clear">Limpar {activeLang.toUpperCase()}</Button>
        )}
      </div>

      {/* Language tabs — one PNG per language */}
      <div className="flex gap-1 mb-3 p-1 bg-slate-100 rounded-md" data-testid="ai-title-lang-tabs">
        {LANGS.map((L) => {
          const hasImage = !!cfg.hero?.[`title_image_url_${L.code}`];
          const isActive = activeLang === L.code;
          return (
            <button
              key={L.code}
              type="button"
              onClick={() => setActiveLang(L.code)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-semibold transition-colors ${
                isActive ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`ai-title-lang-${L.code}`}
            >
              <span>{L.flag}</span>
              <span>{L.label}</span>
              {hasImage && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Tem imagem" />}
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-500 mb-3 flex items-center gap-1">
        <Globe className="w-3 h-3" />
        Cada idioma pode ter uma imagem própria. Se um idioma não tiver a sua imagem, usa a última gerada como fallback.
      </p>

      <div className="space-y-2 mb-3">
        {lines.map((ln, i) => (
          <div key={`ai-title-line-${activeLang}-${i}`} className="flex items-center gap-2" data-testid={`ai-title-row-${i}`}>
            <Input
              value={ln}
              onChange={(e) => updateLine(i, e.target.value)}
              placeholder={`Linha ${i + 1}`}
              className="flex-1"
              data-testid={`ai-title-line-${i}`}
            />
            <div className="w-32">
              <ColorField label="" value={colors[i] || "#FFFFFF"} onChange={(v) => updateColor(i, v)} testid={`ai-title-color-${i}`} />
            </div>
            {lines.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => removeLine(i)} className="text-rose-500 px-2" data-testid={`ai-title-remove-${i}`}>
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
        {lines.length < 5 && (
          <Button variant="outline" size="sm" onClick={addLine} className="text-xs" data-testid="ai-title-add">
            + Adicionar linha
          </Button>
        )}
      </div>

      <div className="flex items-end gap-2 mb-3">
        <div className="w-48">
          <Label className="text-xs text-slate-600">Estilo</Label>
          <select
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
            data-testid="ai-title-style"
          >
            <option value="painted">Painted (graffiti / brush)</option>
            <option value="clean">Clean (smooth poster)</option>
            <option value="neon">Neon (glow tubo)</option>
            <option value="comic">Comic (pop-art halftone)</option>
          </select>
        </div>
        <Button onClick={handleGenerate} disabled={busy} className="flex-1" data-testid="ai-title-generate">
          <Sparkles className="w-4 h-4 mr-1.5" />
          {busy ? `A gerar ${activeLang.toUpperCase()}…` : `Gerar imagem ${activeLang.toUpperCase()} via IA`}
        </Button>
      </div>

      {fullImgUrl && (
        <div
          className="mt-3 rounded-lg p-3 flex items-center justify-center"
          style={{
            backgroundImage:
              "linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)",
            backgroundSize: "16px 16px",
            backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
            backgroundColor: "#f8fafc",
          }}
          data-testid="ai-title-preview"
        >
          <img src={fullImgUrl} alt="preview" className="max-h-56 object-contain" />
        </div>
      )}

      {currentImg && (
        <div className="mt-4">
          <HeroImageControls
            prefix="title_image"
            cfg={cfg}
            setCfg={setCfg}
            defaults={{ width: 560, glow_color: "#000000", glow_intensity: 55 }}
            testid="ai-title"
          />
        </div>
      )}
    </div>
  );
}
